/*
 * View as (draft 14; the old viewas.php): an owner sees one person's day the way that person sees it on their phone,
 * prices hidden unless they have the See job prices tick. Read only; each look is written in the activity log.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { addDays, type MyVisit } from "@steward/shared";
import { api } from "../api/client";
import { useSession } from "../app/session";
import { clock, shortDate, todayIn } from "../components/format";
import { Banner, Button, Card, EmptyState, LoadError, Page, Skeleton } from "../components/parts";

export function ViewAs({ personId }: { personId: string }) {
  const { company } = useSession();
  const [day, setDay] = useState(todayIn(company.timezone));
  const q = useQuery({
    queryKey: ["view-as", company.id, personId, day],
    queryFn: () => api<{ name: string; visits: MyVisit[] }>("GET", `team/${personId}/day?date=${day}`),
  });
  const first = q.data?.name.split(" ")[0] ?? "they";
  return (
    <Page
      title={q.data ? `${q.data.name}'s day` : "Their day"}
      subtitle={shortDate(day)}
      intro="What this person sees on their phone for the day. You can look, not change anything."
      secondaryAction={
        <div className="row">
          <Button type="secondary" icon="chevronLeft" onClick={() => setDay(addDays(day, -1))}>
            Day before
          </Button>
          <Button type="secondary" onClick={() => setDay(addDays(day, 1))}>
            Next day
          </Button>
        </div>
      }
    >
      <Banner>You are seeing what {first} sees. Read only.</Banner>
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !q.data ? (
        <Skeleton rows={4} />
      ) : q.data.visits.length === 0 ? (
        <EmptyState icon="schedule" title="No visits this day">
          Nothing is on {first}&apos;s list for {shortDate(day)}.
        </EmptyState>
      ) : (
        <div className="stack">
          {q.data.visits.map((v) => (
            <Card key={v.ref} title={`${clock(v.startTime)} · ${v.clientName}`}>
              <p>{v.jobName}</p>
              {v.address ? <p className="muted small">{v.address}</p> : null}
              {v.crewNotes ? <p className="small">{v.crewNotes}</p> : null}
              <p className="muted small">With {v.crew.join(", ")}</p>
              <Link to="/hr/team/$personId/visit/$ref" params={{ personId, ref: v.ref }} className="btn secondary small">
                Open the visit as {first} sees it
              </Link>
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}
